import { useState, useEffect, useCallback } from 'react';
import { Modal, IconButton, Box, Typography, Stack, CircularProgress, Tooltip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { MediaCategoryType } from '../../../types/mediaTypes';
import { useLocalization } from '../../../localization';
import notifyHandler from 'utils/notifyHandler';

export interface MediaItem {
  url: string;
  type?: MediaCategoryType;
  title?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  mediaItems: MediaItem[];
  activeIndex?: number;
}
const MEDIA_CONTENT_ID = 'media-content';

const MediaViewModal = (props: Props) => {
  const { isOpen, onClose, mediaItems, activeIndex = 0 } = props;
  const theme = useTheme();
  const { t } = useLocalization();
  const [currentIndex, setCurrentIndex] = useState(activeIndex);
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const handlePrevious = useCallback(() => {
    setCurrentIndex(prevIndex => {
      const newIndex = prevIndex > 0 ? prevIndex - 1 : mediaItems.length - 1;
      setIsLoading(true);
      setIsError(false);
      setScale(1);
      return newIndex;
    });
  }, [mediaItems.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex(prevIndex => {
      const newIndex = prevIndex < mediaItems.length - 1 ? prevIndex + 1 : 0;
      setIsLoading(true);
      setIsError(false);
      setScale(1);
      return newIndex;
    });
  }, [mediaItems.length]);

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      const mediaElement = document.getElementById(MEDIA_CONTENT_ID);
      mediaElement?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleMediaLoad = () => {
    setIsLoading(false);
    setIsError(false);
  };

  const handleMediaError = () => {
    setIsLoading(false);
    setIsError(true);
  };

  const handleCopy = async () => {
    const url = mediaItems?.[currentIndex]?.url;
    if (!url) return;
    await navigator.clipboard.writeText(mediaItems?.[currentIndex]?.url);
    notifyHandler({ type: 'success', content: t('materials.pages.presentation.linkCopyToClipboardSuccess') });
  };

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      switch (e.key) {
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case 'Escape':
          if (isFullscreen) {
            document.exitFullscreen().catch(() => {});
          } else {
            onClose();
          }
          break;
        case 'f':
          toggleFullscreen();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handlePrevious, handleNext, onClose, isFullscreen]);

  const renderMediaContent = () => {
    if (!mediaItems.length) return null;

    const currentItem = mediaItems[currentIndex];
    const mediaType = currentItem?.type || 'image';
    if (isError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
          }}
        >
          <BrokenImageIcon sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="body1">{t('common.messages.fetchingError')}</Typography>
        </Box>
      );
    }
    switch (mediaType) {
      case 'image':
        return (
          <Box
            component="img"
            src={currentItem?.url ?? ''}
            alt={currentItem?.title || `Image ${currentIndex + 1}`}
            sx={{
              height: '90%',
              width: '100%',
              objectFit: 'contain',
              transform: `scale(${scale})`,
              transition: 'transform 0.3s ease',
            }}
            onLoad={handleMediaLoad}
            onError={handleMediaError}
          />
        );
      case 'video':
        return (
          <Box
            component="video"
            src={currentItem.url}
            autoPlay
            controls
            sx={{
              maxHeight: '100%',
              maxWidth: '100%',
              height: '90%',
              transform: `scale(${scale})`,
              transition: 'transform 0.3s ease',
            }}
            onLoad={handleMediaLoad}
            onError={handleMediaError}
          />
        );
      case 'iframe':
      case 'txt':
        return (
          <Box
            allow="xr-spatial-tracking"
            component="iframe"
            src={currentItem.url}
            title={currentItem.title || `Frame ${currentIndex + 1}`}
            allowFullScreen
            onLoad={handleMediaLoad}
            onError={handleMediaError}
            sx={{
              maxHeight: '100%',
              maxWidth: '100%',
              width: '100%',
              height: '90%',
              transform: `scale(${scale})`,
              transition: 'transform 0.3s ease',
            }}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen || !mediaItems.length) return null;

  return (
    <Modal open={isOpen} onClose={onClose} closeAfterTransition>
      <Box
        id={MEDIA_CONTENT_ID}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          outline: 'none',
        }}
      >
        {isLoading && (
          <CircularProgress
            size={64}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'common.white',
            }}
          />
        )}
        {/* Media container */}
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            position: 'relative',
            width: isFullscreen ? '100vw' : '85vw',
            height: isFullscreen ? '100vh' : '85vh',
            overflow: 'hidden',
            backgroundColor: !isFullscreen ? 'common.white' : 'transparent',
            padding: '1rem',
            borderRadius: 4,
          }}
        >
          {renderMediaContent()}

          {/* Bottom control bar */}
          <Stack
            alignItems="flex-end"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              padding: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t('common.actions.copy')} placement="top">
                <IconButton sx={{ color: 'common.black' }} onClick={handleCopy}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                sx={{ color: isFullscreen ? 'common.white' : 'common.black' }}
              >
                <ZoomOutIcon />
              </IconButton>
              <Typography
                sx={{ display: 'flex', alignItems: 'center', color: isFullscreen ? 'common.white' : 'common.black' }}
              >
                {Math.round(scale * 100)}%
              </Typography>
              <IconButton
                onClick={handleZoomIn}
                disabled={scale >= 3}
                sx={{ color: isFullscreen ? 'common.white' : 'common.black' }}
              >
                <ZoomInIcon />
              </IconButton>
              <IconButton onClick={toggleFullscreen} sx={{ color: isFullscreen ? 'common.white' : 'common.black' }}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Box>
          </Stack>
        </Stack>

        {/* Navigation arrows */}
        {mediaItems.length > 1 && (
          <>
            <IconButton
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'common.white',
                backgroundColor: alpha(theme.palette.common.black, 0.5),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.common.black, 0.7),
                },
              }}
              onClick={e => {
                e.stopPropagation();
                handlePrevious();
              }}
              size="large"
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'common.white',
                backgroundColor: alpha(theme.palette.common.black, 0.5),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.common.black, 0.7),
                },
              }}
              onClick={e => {
                e.stopPropagation();
                handleNext();
              }}
              size="large"
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </>
        )}

        {/* Top bar with close button */}
        <Stack
          justifyContent="space-between"
          alignItems="center"
          direction="row"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            padding: 2,
            background: `linear-gradient(
              to bottom,
              ${alpha(theme.palette.common.black, 0.7)} 0%,
              ${alpha(theme.palette.common.black, 0)} 100%
            )`,
          }}
        >
          <Typography color="common.white">
            {`${currentIndex + 1} / ${mediaItems.length}`} <br />
            {mediaItems[currentIndex]?.title ?? ''}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'common.white' }} size="large">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
    </Modal>
  );
};

export default MediaViewModal;
